import { useEffect, useState, useCallback } from 'react';
import llmService from '../services/llmService';

// OpenAI pricing per 1K tokens (input/output)
const MODEL_PRICING = {
  // GPT-5 models (Responses API) - Main variants only
  'gpt-5-chat-latest': { input: 0.015, output: 0.06 },
  
  // GPT-4o models (Chat Completions API)
  'gpt-4o': { input: 0.0025, output: 0.01 },
  'gpt-4o-mini': { input: 0.00015, output: 0.0006 }
};

const formatPrice = (price) => {
  if (price >= 0.001) return price.toFixed(3);
  if (price >= 0.0001) return price.toFixed(4);
  return price.toFixed(5);
};

export function useModelSelector() {
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(() => localStorage.getItem('toneShiftSelectedModel') || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await llmService.getProviders();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch models');
      }

      const models = result.data.results.flatMap(provider => 
        provider.supported_models.map(model => {
          // Better abbreviation logic
          const shortName = provider.name === 'OpenAI' ? 
            model.replace('gpt-5-chat-latest', '5 Chat').replace('gpt-4o-', '4o-').replace('gpt-4-', '4 ').replace('gpt-3.5-', '3.5 ').replace('gpt-', '').replace('-turbo', '') :
            provider.name === 'Claude' ? model.replace('claude-', '') : model;
          
          // Get pricing info
          const pricing = MODEL_PRICING[model];
          const priceDisplay = pricing 
            ? `$${formatPrice(pricing.input)}/$${formatPrice(pricing.output)} per 1K tokens`
            : 'Pricing varies';
          
          return {
            id: `${provider.name}::${model}`,
            name: `${provider.name} - ${model}`,
            short: `${provider.name}: ${shortName}`,
            display: `${provider.name} ${shortName}`,
            provider: provider.name,
            model,
            pricing: priceDisplay,
            providerInfo: provider
          };
        })
      );

      setAvailableModels(models);
      
      // Set default model if none selected - prioritize GPT-4o-mini for cost efficiency
      if (!selectedModel && models.length > 0) {
        const defaultModel = models.find(m => m.model === 'gpt-4o-mini') || 
                            models.find(m => m.model === 'gpt-4o') || 
                            models.find(m => m.model === 'gpt-5-chat-latest') || 
                            models[0];
        setSelectedModel(defaultModel.id);
        localStorage.setItem('toneShiftSelectedModel', defaultModel.id);
      }
    } catch (e) {
      setError(e.message || 'Failed to fetch models');
      setAvailableModels([]);
    } finally {
      setLoading(false);
    }
  }, [selectedModel]);

  useEffect(() => {
    let mounted = true;
    fetchModels().then(() => {
      if (!mounted) return;
    });
    return () => { mounted = false; };
  }, []); // load once

  const select = (id) => {
    setSelectedModel(id);
    localStorage.setItem('toneShiftSelectedModel', id);
  };

  return { availableModels, selectedModel, select, loading, error };
}
